import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Bot, CheckCircle2, XCircle, Wifi, WifiOff, Save, Send, RefreshCw, ChevronDown, ChevronUp, ExternalLink, Zap, MessageSquare, Settings2, Link2, Key } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminOkBot() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);

    // Form state — admin API key
    const [baseUrl, setBaseUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [saving, setSaving] = useState(false);

    // Connect API state
    const [platformCode, setPlatformCode] = useState('');
    const [platformKey, setPlatformKey] = useState('');
    const [savingConnect, setSavingConnect] = useState(false);

    // Test connection state
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    // Chat test state
    const [chatMsg, setChatMsg] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [sending, setSending] = useState(false);
    const [showCapabilities, setShowCapabilities] = useState(false);
    const [showEndpoints, setShowEndpoints] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/okbot/settings');
            setCfg(data);
            setBaseUrl(data.base_url || 'https://okbot.okgo.mx');
            setEnabled(data.enabled);
            // Pre-fill platform_code from server (seeded on startup)
            if (data.platform_code) setPlatformCode(data.platform_code);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401 || status === 403) {
                toast.error('Sesión expirada — vuelve a iniciar sesión');
            } else {
                toast.error('Error al cargar configuración OkBot');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { base_url: baseUrl, enabled };
            if (apiKey) payload.api_key = apiKey;
            await api.post('/admin/okbot/settings', payload);
            toast.success('Configuración guardada');
            setApiKey('');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const saveConnect = async () => {
        if (!platformCode.trim()) {
            toast.error('El Platform Code es obligatorio');
            return;
        }
        setSavingConnect(true);
        try {
            const payload = { platform_code: platformCode.trim(), enabled };
            if (platformKey) payload.platform_key = platformKey;
            await api.post('/admin/okbot/settings', payload);
            toast.success('Credenciales Connect API guardadas');
            setPlatformKey('');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar credenciales Connect');
        } finally {
            setSavingConnect(false);
        }
    };

    const testConn = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post('/admin/okbot/test');
            setTestResult({ ok: true, ...data });
        } catch (e) {
            setTestResult({ ok: false, error: e?.response?.data?.detail || 'Sin respuesta' });
        } finally {
            setTesting(false);
        }
    };

    const sendChat = async (e) => {
        e.preventDefault();
        if (!chatMsg.trim()) return;
        const msg = chatMsg.trim();
        setChatMsg('');
        setChatHistory(h => [...h, { role: 'user', text: msg }]);
        setSending(true);
        try {
            const { data } = await api.post('/admin/okbot/message', { message: msg });
            const reply = data?.reply || data?.message || data?.response || data?.text || JSON.stringify(data);
            setChatHistory(h => [...h, { role: 'bot', text: reply }]);
        } catch (e) {
            const err = e?.response?.data?.detail || 'Error al contactar OkBot';
            setChatHistory(h => [...h, { role: 'error', text: err }]);
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-48 text-muted-foreground">Cargando…</div>;

    const isConnected = cfg?.has_api_key;
    const hasConnectCreds = cfg?.has_platform_key && cfg?.platform_code;

    return (
        <div className="space-y-6 max-w-3xl">

            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-xl bg-[#E53935] flex items-center justify-center">
                            <Bot className="h-5 w-5 text-white" />
                        </div>
                        <h1 className="text-xl font-bold">OkBot — Agente IA</h1>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        Conecta OkBot como el agente principal de inteligencia artificial de OkLoVendo.
                    </p>
                </div>
                <a
                    href="https://okbot.okgo.mx"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                >
                    <ExternalLink className="h-3 w-3" /> okbot.okgo.mx
                </a>
            </div>

            {/* Status banner */}
            <div className={`rounded-lg border p-4 flex items-center gap-3 ${isConnected && cfg?.enabled ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800'}`}>
                {isConnected && cfg?.enabled ? (
                    <><CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" /><div><p className="text-sm font-semibold text-green-700 dark:text-green-400">OkBot configurado</p><p className="text-xs text-green-600 dark:text-green-500">Clave API activa · {baseUrl}</p></div></>
                ) : (
                    <><WifiOff className="h-5 w-5 text-yellow-600 shrink-0" /><div><p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">Sin configurar</p><p className="text-xs text-yellow-600 dark:text-yellow-500">Ingresa la clave API de OkBot para activar el agente</p></div></>
                )}
            </div>

            {/* Settings card — Admin API key */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2 font-semibold text-sm">
                    <Settings2 className="h-4 w-4" /> Configuración de conexión (Admin API)
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">URL base de OkBot</label>
                        <input
                            value={baseUrl}
                            onChange={e => setBaseUrl(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            placeholder="https://okbot.okgo.mx"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                            Clave API {cfg?.has_api_key && <span className="text-green-600">(configurada: {cfg.api_key_masked})</span>}
                        </label>
                        <input
                            type="password"
                            value={apiKey}
                            onChange={e => setApiKey(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            placeholder={cfg?.has_api_key ? 'Escribe para cambiar la clave actual' : 'okbot_...'}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="okbot-enabled"
                            checked={enabled}
                            onChange={e => setEnabled(e.target.checked)}
                            className="rounded"
                        />
                        <label htmlFor="okbot-enabled" className="text-sm">Agente OkBot habilitado en la plataforma</label>
                    </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                    <button
                        onClick={save}
                        disabled={saving}
                        className="flex items-center gap-1.5 bg-[#E53935] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#c62828] disabled:opacity-50"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {saving ? 'Guardando…' : 'Guardar'}
                    </button>
                    <button
                        onClick={testConn}
                        disabled={testing || !cfg?.has_api_key}
                        className="flex items-center gap-1.5 border px-4 py-2 rounded-md text-sm font-medium hover:bg-secondary disabled:opacity-50"
                    >
                        {testing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />}
                        {testing ? 'Verificando…' : 'Probar conexión'}
                    </button>
                </div>

                {/* Test result */}
                {testResult && (
                    <div className={`rounded-md p-3 text-sm flex items-start gap-2 ${testResult.ok ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                        {testResult.ok
                            ? <><CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /><div><strong>Conexión exitosa</strong> · {testResult.latency_ms}ms · Estado: {testResult.status}</div></>
                            : <><XCircle className="h-4 w-4 shrink-0 mt-0.5" /><div><strong>Error:</strong> {testResult.error}</div></>
                        }
                    </div>
                )}
            </div>

            {/* Connect API credentials */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-semibold text-sm">
                        <Link2 className="h-4 w-4 text-blue-500" /> Connect API — Sincronización de usuarios y wallet
                    </div>
                    {hasConnectCreds ? (
                        <span className="text-xs bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400 border border-green-200 dark:border-green-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Activa
                        </span>
                    ) : (
                        <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full">
                            Pendiente
                        </span>
                    )}
                </div>

                <p className="text-xs text-muted-foreground">
                    La Connect API permite sincronizar usuarios y saldos OkWallet con OkBot usando credenciales de plataforma (
                    <code className="font-mono bg-muted px-1 rounded">X-Platform-Code</code> +{' '}
                    <code className="font-mono bg-muted px-1 rounded">X-Platform-Key</code>).
                    OkLovendo ya está registrado en OkBot como plataforma vinculada.
                    Solicita la <strong>Platform Key</strong> al equipo OkGo e ingrésala aquí.
                </p>

                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                            Platform Code{' '}
                            {cfg?.platform_code && (
                                <span className="text-blue-600 dark:text-blue-400">(actual: <code className="font-mono">{cfg.platform_code}</code>)</span>
                            )}
                        </label>
                        <input
                            value={platformCode}
                            onChange={e => setPlatformCode(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background font-mono"
                            placeholder="OK0508HKVP7MG"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Código asignado por OkBot al vincular OkLovendo. No cambia salvo que se revincule.
                        </p>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                            Platform Key (X-Platform-Key){' '}
                            {cfg?.has_platform_key && (
                                <span className="text-green-600">(configurada: {cfg.platform_key_masked})</span>
                            )}
                        </label>
                        <div className="relative">
                            <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <input
                                type="password"
                                value={platformKey}
                                onChange={e => setPlatformKey(e.target.value)}
                                className="w-full border rounded-md pl-8 pr-3 py-2 text-sm bg-background font-mono"
                                placeholder={cfg?.has_platform_key ? 'Escribe para cambiar la clave actual' : 'okbot_7vDf… (pedir al equipo OkGo)'}
                            />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            Clave secreta de la plataforma. Visible solo en el panel de admin de OkBot bajo el nombre "oklovendo".
                        </p>
                    </div>
                </div>

                {/* Connect API status info */}
                {!hasConnectCreds && (
                    <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-700 dark:text-amber-400">
                        <strong>¿Cómo obtener la Platform Key?</strong>
                        <ol className="list-decimal list-inside mt-1 space-y-0.5">
                            <li>Abre <a href="https://okbot.okgo.mx" target="_blank" rel="noreferrer" className="underline">okbot.okgo.mx</a> → Admin → API Keys</li>
                            <li>Localiza la clave con nombre <strong>"oklovendo"</strong> (prefijo <code className="font-mono bg-amber-100 dark:bg-amber-900 px-0.5 rounded">okbot_7vDfYfZT8…</code>)</li>
                            <li>Si no es visible completa, pide al equipo OkGo que la resetee o comparta</li>
                            <li>Ingresa el valor completo en el campo de arriba y guarda</li>
                        </ol>
                    </div>
                )}

                <div className="pt-1">
                    <button
                        onClick={saveConnect}
                        disabled={savingConnect || !platformCode.trim()}
                        className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {savingConnect ? 'Guardando…' : 'Guardar credenciales Connect'}
                    </button>
                </div>
            </div>

            {/* Capabilities */}
            {cfg?.capabilities?.length > 0 && (
                <div className="rounded-lg border bg-card overflow-hidden">
                    <button
                        className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold hover:bg-secondary/50"
                        onClick={() => setShowCapabilities(v => !v)}
                    >
                        <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Capacidades de OkBot ({cfg.capabilities.length})</div>
                        {showCapabilities ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showCapabilities && (
                        <div className="px-5 pb-4 flex flex-wrap gap-2">
                            {cfg.capabilities.map(c => (
                                <span key={c} className="text-xs bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 px-2 py-0.5 rounded-full font-mono">
                                    {c}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Endpoints */}
            {cfg?.endpoints?.length > 0 && (
                <div className="rounded-lg border bg-card overflow-hidden">
                    <button
                        className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold hover:bg-secondary/50"
                        onClick={() => setShowEndpoints(v => !v)}
                    >
                        <div className="flex items-center gap-2"><ExternalLink className="h-4 w-4 text-purple-500" /> Endpoints registrados ({cfg.endpoints.length})</div>
                        {showEndpoints ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showEndpoints && (
                        <div className="px-5 pb-4 space-y-2">
                            {cfg.endpoints.map((ep, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs">
                                    <span className={`font-mono px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${ep.method === 'GET' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{ep.method}</span>
                                    <span className="font-mono text-muted-foreground">{ep.path}</span>
                                    {ep.auth === 'platform_key' && (
                                        <span className="shrink-0 text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800 px-1.5 py-0.5 rounded font-mono">
                                            Connect API
                                        </span>
                                    )}
                                    <span className="text-muted-foreground hidden sm:inline">— {ep.description}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Chat test */}
            <div className="rounded-lg border bg-card overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b font-semibold text-sm">
                    <MessageSquare className="h-4 w-4" /> Prueba de chat con OkBot
                    {!cfg?.has_api_key && <span className="text-xs text-muted-foreground font-normal ml-1">(configura la clave API primero)</span>}
                </div>
                <div className="h-52 overflow-y-auto px-4 py-3 space-y-2 bg-muted/30">
                    {chatHistory.length === 0 && (
                        <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                            Escribe un mensaje para probar el agente OkBot
                        </div>
                    )}
                    {chatHistory.map((m, i) => (
                        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                                m.role === 'user'
                                    ? 'bg-[#E53935] text-white'
                                    : m.role === 'error'
                                    ? 'bg-red-100 text-red-700 border border-red-200'
                                    : 'bg-card border text-foreground'
                            }`}>
                                {m.text}
                            </div>
                        </div>
                    ))}
                    {sending && (
                        <div className="flex justify-start">
                            <div className="bg-card border rounded-xl px-3 py-2 text-sm text-muted-foreground">
                                OkBot está escribiendo…
                            </div>
                        </div>
                    )}
                </div>
                <form onSubmit={sendChat} className="flex gap-2 p-3 border-t">
                    <input
                        value={chatMsg}
                        onChange={e => setChatMsg(e.target.value)}
                        disabled={sending || !cfg?.has_api_key}
                        className="flex-1 border rounded-md px-3 py-2 text-sm bg-background disabled:opacity-50"
                        placeholder={cfg?.has_api_key ? 'Escribe un mensaje…' : 'Configura la clave API para chatear'}
                    />
                    <button
                        type="submit"
                        disabled={sending || !cfg?.has_api_key || !chatMsg.trim()}
                        className="flex items-center gap-1.5 bg-[#E53935] text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-[#c62828] disabled:opacity-50"
                    >
                        <Send className="h-3.5 w-3.5" />
                        {sending ? '…' : 'Enviar'}
                    </button>
                </form>
            </div>

            {/* Info box */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4 text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <p className="font-semibold">¿Cómo funciona OkBot como agente principal?</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs text-blue-600 dark:text-blue-400">
                    <li>Los usuarios de OkLoVendo pueden chatear con OkBot directamente desde la plataforma.</li>
                    <li>OkBot recibe contexto del marketplace (búsquedas, anuncios, carrito) para dar respuestas relevantes.</li>
                    <li>Las sesiones de chat se gestionan con <code className="font-mono bg-blue-100 dark:bg-blue-900 px-1 rounded">session_id</code> para mantener conversaciones continuas.</li>
                    <li>Con la <strong>Connect API</strong> activa, los registros de usuario y saldos OkWallet se sincronizan automáticamente.</li>
                </ul>
            </div>
        </div>
    );
}
